import { useState } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Copy, RefreshCw, FileText } from 'lucide-react';
import { toast } from 'sonner';

const RobotsTxtGeneratorTool = () => {
  const [sitemapUrl, setSitemapUrl] = useState('');
  const [allowAll, setAllowAll] = useState(true);
  const [disallowPaths, setDisallowPaths] = useState('/admin\n/private');
  const [crawlDelay, setCrawlDelay] = useState('');

  const generateRobotsTxt = () => {
    let content = '# Robots.txt generated by ToolsForge\n\n';
    content += 'User-agent: *\n';
    
    if (allowAll) {
      content += 'Allow: /\n';
    }
    
    if (disallowPaths.trim()) {
      const paths = disallowPaths.split('\n').filter(p => p.trim());
      paths.forEach(path => {
        content += `Disallow: ${path.trim()}\n`;
      });
    }
    
    if (crawlDelay) {
      content += `Crawl-delay: ${crawlDelay}\n`;
    }
    
    if (sitemapUrl.trim()) {
      content += `\nSitemap: ${sitemapUrl.trim()}\n`;
    }
    
    return content;
  };

  const output = generateRobotsTxt();

  const copyOutput = () => {
    navigator.clipboard.writeText(output);
    toast.success('robots.txt copied!');
  };

  const downloadFile = () => {
    const blob = new Blob([output], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'robots.txt';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Downloaded robots.txt');
  };

  return (
    <div className="space-y-6" data-testid="robots-txt-tool">
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <label className="text-sm text-muted-foreground mb-2 block">Sitemap URL</label>
            <Input
              placeholder="https://example.com/sitemap.xml"
              value={sitemapUrl}
              onChange={(e) => setSitemapUrl(e.target.value)}
            />
          </div>

          <div className="flex gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={allowAll}
                onChange={(e) => setAllowAll(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm">Allow all crawling</span>
            </label>
          </div>

          <div>
            <label className="text-sm text-muted-foreground mb-2 block">
              Disallow Paths (one per line)
            </label>
            <Textarea
              placeholder="/admin&#10;/private&#10;/api"
              value={disallowPaths}
              onChange={(e) => setDisallowPaths(e.target.value)}
              className="min-h-[100px] font-mono text-sm"
            />
          </div>

          <div>
            <label className="text-sm text-muted-foreground mb-2 block">
              Crawl Delay (seconds, optional)
            </label>
            <Input
              type="number"
              placeholder="10"
              value={crawlDelay}
              onChange={(e) => setCrawlDelay(e.target.value)}
            />
          </div>
        </div>

        <div>
          <label className="text-sm text-muted-foreground mb-2 block flex items-center gap-2">
            <FileText className="w-4 h-4 text-primary" />
            Generated robots.txt
          </label>
          <Textarea
            value={output}
            readOnly
            className="min-h-[300px] font-mono text-sm bg-muted/50"
          />
        </div>
      </div>

      <div className="flex gap-2">
        <Button onClick={copyOutput}>
          <Copy className="w-4 h-4 mr-2" /> Copy
        </Button>
        <Button onClick={downloadFile} variant="outline">
          Download robots.txt
        </Button>
        <Button onClick={() => { setSitemapUrl(''); setDisallowPaths(''); setCrawlDelay(''); }} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" /> Reset
        </Button>
      </div>
    </div>
  );
};

export default RobotsTxtGeneratorTool;
